import { connect } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { useIntl } from "react-intl";
import List from "../../../common/list";
import { TextInput } from "../../../common/fields";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import {
  adminListRequest,
  setQuery,
  performActionRequest,
} from "../../../../store/slices/usersSlice";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { AccessScopeConstants, RouteConstants } from "../../../../constants";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import CustomBreadCrumbs from "../../../common/breadcrumbs";

const AdminUserList = ({
  action,
  labels,
  list,
  listRequest,
  query,
  requesting,
  setQuery,
  user,
  performActionRequest,
  error,
  totalCount,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [namePattern, setNamePattern] = useState("");
  const [selectedRecords, setSelectedRecords] = useState([]);

  const { role } = user || {};

  const canAdd = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.CreateScope
  );

  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.UpdateScope
  );

  const canDelete = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.DeleteScope
  );

  const fetchList = useCallback(() => {
    listRequest(query);
  }, [query, listRequest]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (
      !requesting &&
      !error &&
      (action === "delete" || action === "activate" || action === "deactivate")
    ) {
      if (action === "delete") {
        setQuery({ pageNo: 1 });
      } else {
        fetchList();
      }
    }
  }, [action, requesting, error, setQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query?.namePattern !== namePattern) {
        setQuery({ namePattern, pageNo: 1 });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [namePattern, setQuery, query]);

  const onSelectionChange = (selectedRecords) => {
    setSelectedRecords(selectedRecords?.value || []);
  };

  const handlePageChange = (pageData) => {
    if (query.pageSize !== pageData.rows) {
      setQuery({ ...query, pageNo: 1, pageSize: pageData.rows });
    }
  };

  const handleOnSortChange = (sortData) => {
    setQuery({
      ...query,
      pageNo: 1,
      sortColumn: sortData.sortField,
      sortBy: sortData.sortOrder === 1 ? "asc" : "desc",
    });
  };

  const handleAddUser = () => {
    navigate(RouteConstants.APP_ROUTE_USERS_ADD);
  };

  const onDeleteAction = (data) => {
    confirmDialog({
      message: intl.formatMessage({ id: "delete_message" }),
      header: intl.formatMessage({ id: "delete_confirmation" }),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => {
        performActionRequest({ action: "delete", id: data.id });
      },
      reject: () => {},
    });
  };

  return (
    <div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "users" })}
          </div>
          <div className="edexlms-page-actions">
            <Button
              icon="pi pi-plus"
              onClick={handleAddUser}
              size="small"
              label={intl.formatMessage({ id: "add_user" })}
              type="button"
              disabled={!canAdd}
              tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
              tooltip={
                canAdd ? "" : intl.formatMessage({ id: "no_permission" })
              }
            />
          </div>
        </div>
        <div className="edexlms-page-body">
          <div className="edexlms-search-filter-bar">
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({ id: "search_users" })}
              input={{
                name: "search",
                value: namePattern,
                onChange: (e) => setNamePattern(e.target.value),
              }}
            />
          </div>
          <List
            totalRecords={totalCount}
            loading={action === "list" && requesting}
            onSelectionChange={onSelectionChange}
            handlePageChange={handlePageChange}
            handleOnSortChange={handleOnSortChange}
            data={list || []}
            selectedRecords={selectedRecords}
            pageQuery={query}
            selectionMode=""
            columns={[
              {
                field: "name",
                header: "name",
                sortable: true,
                body: ListCellRenders.NameAndEmail(),
              },
              { field: "role.name", header: "role" },
              {
                field: "designation",
                header: "designation",
                sortable: true,
                body: ListCellRenders.StringData("designation"),
              },
              {
                field: "department",
                header: "department",
                sortable: true,
                body: ListCellRenders.StringData("department"),
              },
              {
                field: "isActive",
                header: "status",
                sortable: true,
                body: ListCellRenders.Status,
              },
              {
                field: "actions",
                headerIcon: "pi-cog",
                align: "center",
                body: ListCellRenders.OtherActions(
                  [
                    {
                      label: "view",
                      icon: "pi-eye",
                      isAllowed: true,
                      action: (data) =>
                        navigate(
                          `${RouteConstants.APP_ROUTE_USERS_EDIT.replace(
                            ":id",
                            data.id
                          )}`
                        ),
                    },
                    {
                      label: "delete",
                      icon: "pi-trash",
                      isAllowed: canDelete,
                      action: (data) => onDeleteAction(data),
                    },
                    {
                      label: "activate",
                      icon: "pi-check",
                      isAllowed: canUpdate,
                      action: (data) =>
                        performActionRequest({
                          action: "activate",
                          id: data.id,
                        }),
                      hidden: {
                        field: "isActive",
                        value: true,
                      },
                    },
                    {
                      label: "deactivate",
                      icon: "pi-times",
                      isAllowed: canUpdate,
                      action: (data) =>
                        performActionRequest({
                          action: "deactivate",
                          id: data.id,
                        }),
                      hidden: {
                        field: "isActive",
                        value: false,
                      },
                    },
                  ],
                  "id",
                  user?.details?.id
                ),
              },
            ]}
          />
        </div>
      </div>
      <ConfirmDialog />
    </div>
  );
};

const mapStateToProps = (state) => ({
  list: state?.users?.data?.list || [],
  totalCount: state?.users?.data?.totalCount || 0,
  action: state?.users?.action,
  requesting: state?.users?.requesting,
  error: state?.users?.error,
  query: state?.users?.query,
  user: state?.self?.user,
});

const mapDispatchToProps = (dispatch) => ({
  listRequest: (data) => dispatch(adminListRequest(data)),
  setQuery: (data) => dispatch(setQuery(data)),
  performActionRequest: (data) => dispatch(performActionRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminUserList);
